import LandingPage from "./pages/landing-page/LandingPage";

import { createBrowserRouter, Navigate, replace, useParams } from "react-router-dom";
import RouteDoesntExist from "./fallbacks/RouteDoesntExist";
import ProviderDashboard from "./pages/provider/dashboard/ProviderDashboard";
import PublicLayoutFinalization from "./layouts/public-layout-finalization/PublicLayoutFinalization";
import SignUpProvider from "./pages/provider/sign-up/SignUpProvider";
import SignUpProviderConfirmation from "./pages/provider/sign-up-confimation/SignUpProviderConfirmation";
import LoginProvider from "./pages/login/Login";
import ForgotPassword from "./pages/forgotPassword/ForgotPassword";
import CompanyFirstLogin from "./pages/provider/company-first-login/CompanyFirstLogin";
import ProductsPage from "./pages/provider/products/ProductsPage";
import ProviderGuard from "./guards/ProviderGuard";
import CreateProduct from "./pages/provider/products/CreateProduct";
import EditProduct from "./pages/provider/products/EditProduct";
import LogoutHandler from "./LogoutHandler";
import TeamMembers from "./pages/provider/team-member/TeamMembers";
import AddTeamMember from "./pages/provider/team-member/add-team-member/AddTeamMember";
import ConfirmTeamMember from "./pages/provider/team-member/confirm-team-member/ConfirmTeamMember";

import { ApplicationRoutes } from "./routes";
import AdminGuard from "./guards/AdminGuard";
import SolarProvider from "./pages/admin/solar-provider/SolarProvider";
import SearchParameters from "./pages/admin/appliances/SearchParameters";
import LoanProductsPage from "./pages/admin/loan-products/LoanProductsPage";
import LoanRequestPage from "./pages/admin/loan-request/LoanRequestPage";
import ProductBrowserPage from "./pages/customer/product-browser/ProductBrowserPage";
import PublicLayoutProducts from "./layouts/public-layout-products/PublicLayoutProducts";
import SolarRecommendation from "./pages/customer/solar-recommendation/SolarRecommendation";
import ProductListPage from "./pages/customer/product-list/ProductListPage";
import SolarSystemDetails from "./pages/customer/solar-system-details/SolarSystemDetails";
import SolarProductConfirmation from "./pages/customer/solar-system-details/solar-product-confirmation/SolarProductConfirmation";
import CustomerGuard from "./guards/CustomerGuard";
import { FeatureGuard } from "./tenant/features/FeatureGuard";
import CustomerDashboard from "./pages/customer/dashboard/CustomerDashboardPage";
import CustomerOrders from "./pages/customer/orders/CustomerOrdersPage";
import ProviderCustomers from "./pages/provider/customer/CustomerPage";
import AdminCustomerPage from "./pages/admin/customer/AdminCustomerPage";
import CustomerPaymentsPage from "./pages/customer/payments/CustomerPaymentsPage";
import AdminDashboard from "./pages/admin/dashboard/AdminDashboard";
import LoanSummaryPage from "./pages/admin/loan-summary/LoanSummaryPage";
import PublicLayoutLanding from "./layouts/public-layout-landing/PublicLayoutLanding";
import Terms from "./pages/terms";
import ComingSoonPage from "./pages/coming-soon";
import PublicFormLayout from "./layouts/public-form-layout/PublicFormLayout";
import UserInfoStep from "./pages/sign-up/UserInfoStep";
import VerifyOtpStep from "./pages/sign-up/VerifyOtpStep";
import SignUpSelectNextStep from "./pages/sign-up/SignUpSelectNextStep";
import TestCanvas from "./pages/test/TestCanvas";
import StoreFrontLayout from "./pages/store-front/layout/StoreFrontLayout";
import StoreFront from "./pages/store-front/StoreFront";
import StoreFrontItem from "./pages/store-front/item/StoreFrontItem";
import ProductCategoryPage from "./pages/admin/products/category";
import SelectNextStep from "./pages/next-step/SelectNextStep";
import StoreFrontCategory from "./pages/store-front/category/StoreFrontCategory";
import RoofTypePage from "./pages/admin/roof-type";
import AdvertBannerPage from "./pages/admin/advert-banner/AdvertBannerPage";
import DepartmentsPage from "./pages/admin/departments/DepartmentsPage";
import CreateDepartmentPage from "./pages/admin/departments/CreateDepartmentPage";
import DepartmentDetailPage from "./pages/admin/departments/DepartmentDetailPage";
import ConfirmOrder from "./pages/store-front/confirm-order/ConfirmOrder";
import ScrollToTop from "./common/components/scroll-to-top/ScrollToTop";
import Checkout from "./pages/store-front/checkout/Checkout";
import GuestOrCustomerGuard from "./guards/GuestOrCustomerGuard";
import WishlistPage from "./pages/store-front/wishlist";
import BillingInfoPage from "./pages/store-front/billing-info";
import ConfirmPayment from "./pages/store-front/confirm-payment/ConfirmPayment";
import ApplianceCategory from "./pages/admin/appliances/ApplianceCategory";
import LeadsPage from "./pages/admin/leads/LeadsPage";
import RequestPage from "./pages/provider/request/RequestPage";
import AdminLandingPage from "./pages/admin/landing-page/AdminLandingPage";
import { BpsAdminDashboard } from "./pages/bps-admin/dashboard/BpsAdminDashboard";
import BpsAdminLayout from "./layouts/bps-admin-layout/BpsAdminLayout";
import { BpsAdminPlaceholderPage } from "./pages/bps-admin/placeholder/BpsAdminPlaceholderPage";
import ProductAnalyticsPage from "./pages/bps-admin/product-analytics/ProductAnalyticsPage";
import FunnelsPage from "./pages/bps-admin/product-analytics/funnels/FunnelsPage";
import UtilitySetupPage from "./pages/bps-admin/utility-setup/UtilitySetupPage";
import UtilityDetailsPage from "./pages/bps-admin/utility-setup/utility-details/UtilityDetailsPage";
import BulkPurchasePage from "./pages/admin/bulk-purchase/BulkPurchasePage";
import BulkPurchaseDetailsPage from "./pages/admin/bulk-purchase/BulkPurchaseDetailsPage";
import SettingsPage from "./pages/admin/bills/settings/SettingsPage";
import BillsLayout from "./layouts/bills-layout/BillsLayout";
import { VendingPage } from "./pages/admin/bills/vending/VendingPage";
import { PostpaidPage } from "./pages/admin/bills/postpaid/PostpaidPage";
import { BillsDashboard } from "./pages/admin/bills/bills-dashboard/BillsDashboard";
import PrivacyPolicyPage from "./pages/terms/PrivacyPolicy";
import DeleteInstructionPage from "./pages/terms/DeleteInstruction";
import { LandingPageV2 } from "./pages/landing-page-v2/LandingPageV2";
import UtilityAdminGuard from "./guards/UtilityAdminGuard";
import { UtilAdminDashboard } from "./pages/utility-admin/bills-operations/dashboard/dashboard";
import { CustomerPage } from "./pages/utility-admin/bills-operations/customer/CustomerPage";
import { Reconcilliation } from "./pages/utility-admin/bills-operations/reconcilliation/Reconcilliation";
import RefundsPage from "./pages/utility-admin/bills-operations/refunds/RefundsPage";
import RefundDetailsPage from "./pages/utility-admin/bills-operations/refunds/RefundDetailsPage";
import ActivityLogPage from "./pages/utility-admin/service-request/activity-log/ActivityLogPage";
import UtilityLayout from "./layouts/utility-layout/UtilityLayout";
import CompleteSignup from "./pages/utility-admin/complete-signup/CompleteSignup";
import { VendingPage as VendingPageUtilityAdmin } from "./pages/utility-admin/bills-operations/vending/VendingPage";
import { PostpaidPage as PostpaidPageUtilityAdmin } from "./pages/utility-admin/bills-operations/postpaid/PostpaidPage";
import UtilSettingsPage from "./pages/utility-admin/service-request/settings/UtilSettingsPage";
import { AdminUtilAuditLogPage } from "./pages/utility-admin/service-request/audit-log/AdminUtilAuditLogPage";
import BulkPurchasePageUtilityAdmin from "./pages/utility-admin/bills-operations/bulk-purchase/BulkPurchasePage";
import { withAccess } from "./utils/applyPermissionGuardsToRoutes";
import { UtilityPermissions } from "./model/permissions/permissions";
import { FeatureFlagName } from "./tenant/features/featureFlags";
import CustomFormPage from "./pages/utility-admin/service-request/service-requests/custom-form/CustomFormPage";
import NewServiceTypePage from "./pages/utility-admin/service-request/service-requests/new-service-type/NewServiceTypePage";
import ServiceRequestTypesPage from "./pages/utility-admin/service-request/service-requests/service-request-types/ServiceRequestTypesPage";
import PaymentOptionPage from "./pages/utility-admin/service-request/service-requests/payment-option/PaymentOptionPage";
import ServiceRequestInstancesPage from "./pages/utility-admin/service-request/service-requests/service-request-instances/ServiceRequestInstancesPage";
import ServiceRequestDetailsPage from "./pages/utility-admin/service-request/service-requests/service-request-details/ServiceRequestDetailsPage";
import { CustomerPublicLayout } from "./layouts/customer-public-layout/CustomerPublicLayout";
import WelcomeScreen from "./pages/customer/onboarding/welcome-screen/welcome-screen";
import { CustomerSignInPage } from "./pages/customer/onboarding/customer-signin";
import { CustomerForgotPasswordPage } from "./pages/customer/onboarding/forgot-password/forgot-password-page";
import { CustomerPassCodeLoginPage } from "./pages/customer/onboarding/customer-passcode-signin";
import CustomerLayout from "./layouts/customer-layout/CustomerLayout";
import { CustomerSignUpPage } from "./pages/customer/onboarding/customer-signup";
import { PasswordResetSuccessPage } from "./pages/customer/onboarding/password-reset-success/password-reset-success";
import { CustomerTransactionFilterPage } from "./pages/customer/history/filter-page/CustomerTransactionFilterPage";
import {
	BuyElectricityPage,
	CustomerBulkPurchasePage,
	TransferUnitIntroPage,
	TransferUnitVerifyMeterPage,
	TransferUnitRecipientPage,
	TransferUnitAmountPage,
	CustomerTransactionHistoryDetailsPage,
	CustomerTransactionHistoryPage,
	ServiceRequestPage,
	AllServiceRequestsPage,
	ServiceRequestDetailPage,
	SubmitServiceRequestPage,
	EnergyManagement,
	UnitThresholdSetupPage,
	ConsumptionAnalyticsPage,
	ProfileDetailsPage,
	SettingsLayout,
	SettingsRedirect,
	AppearancePage,
	TeamManagementPage,
	InviteTeamMemberPage,
	ManageTeamMembersPage,
	TeamMemberSubmitDetailsPage,
	PayRequestPage,
	GuestBillPaymentPage,
	GetSolarPage,
	ProfilePicturePage,
	SolarConsumption,
	EstimatedBill,
	NotificationPage,
	SavedBeneficiariesPage,
	NotificationDetailPage,
	ServiceRequestPaymentPage,
	AccountActivitiesPage,
	AccountActivitiesNinVerificationPage,
	HistoricalPaymentPage,
	HistoricalPaymentDetailPage,
} from "./pages/customer";
import EnergyProfileGuard from "./guards/EnergyProfileGuard";
import WorkflowsManagmentPage from "./pages/utility-admin/service-request/workflows/workflows-managment/WorkflowsManagmentPage";
import NewWorkflowPage from "./pages/utility-admin/service-request/workflows/new-workflow/NewWorkflowPage";
import CustomStepsPage from "./pages/utility-admin/service-request/workflows/custom-steps/CustomStepsPage";
import StepInstancePage from "./pages/utility-admin/service-request/step-instance/StepInstancePage";
import { StepsPage } from "./pages/utility-admin/service-request/steps/StepsPage";
import { DepartmentSelectionPage } from "./pages/utility-admin/service-request/steps/DepartmentSelectionPage";
import ModuleSelectionPage from "./pages/utility-admin/module-selection/ModuleSelectionPage";
import { ServiceRequestDashboardPage } from "./pages/utility-admin/service-request/service-request-dashboard/ServiceRequestDashboardPage";
import SuperAgentCreateServiceRequestPage from "./pages/utility-admin/support-agent/CreateServiceRequest";
import { CustomerProfilePage } from "./pages/utility-admin/client-management/CustomerProfilePage";
import { MetersAccountsPage } from "./pages/utility-admin/client-management/MetersAccountsPage";
import { BillingHistoryPage } from "./pages/utility-admin/client-management/BillingHistoryPage";
import { ServiceRequestHistoryPage } from "./pages/utility-admin/client-management/ServiceRequestHistoryPage";
import { InteractionLogsPage } from "./pages/utility-admin/client-management/InteractionLogsPage";
import UnknownCustomerLayout from "./layouts/unknown-customer-layout/unknown-customer-layout";
import { SolarJourneyPage } from "./pages/customer/solar-journey/SolarJourneyPage";
import { SystemReferralPage } from "./pages/utility-admin/referral-system/SystemReferralPage";
import StaffDetailsPage from "./pages/utility-admin/referral-system/StaffDetailsPage";
import AdminFeatureGate from "./pages/utility-admin/component/layout/AdminFeatureGate";
import AllServiceRequestInstancesPage from "./pages/utility-admin/service-request/service-requests/all-service-request-instances/AllServiceRequestInstancesPage";
import { WalletPage } from "./pages/customer/wallet/WalletPage";
import { WalletHistoryPage } from "./pages/customer/wallet/wallet-history/WalletHistoryPage";
import { SupportPage } from "./pages/customer/support/SupportPage";
import { CallCenter } from "./pages/customer/support/call-center/CallCenter";
import { SupportLayout } from "./pages/customer/support/SupportLayout";
import { EnergyProfileWelcomePage } from "./pages/customer/settings/energy-profile/energy-profile-welcome-page/EnergyProfileWelcomePage";
import { EnergyProfileCreatePage } from "./pages/customer/settings/energy-profile/energy-profile-create-page/EnergyProfileCreatePage";
import { NinVerificationPage } from "./pages/customer/settings/energy-profile/nin-verification/NinVerificationPage";
import { ManageMetersPage } from "./pages/customer/energy-profile/manage-meters/ManageMetersPage";
import { SecurityPage } from "./pages/customer/settings/security/SecurityPage";
import { AppInformationPage } from "./pages/customer/settings/app-information/AppInformationPage";
import { RemoteTokenPage } from "./pages/customer/settings/remote-token/RemoteTokenPage";
import { FaqPage } from "./pages/customer/settings/faq/FaqPage";
import { LegalPage } from "./pages/customer/settings/legal/LegalPage";
import { AddBeneficiaryPage } from "./pages/customer/saved-beneficiaries/add-beneficiary";
import { BeneficiaryDetailsPage } from "./pages/customer/saved-beneficiaries/beneficiary-details";
import SwitchMeterNumberPage from "./pages/customer/settings/energy-profile/switch-meter-number/SwitchMeterNumberPage";
import ProviderLayout from "./layouts/private-layout/ProviderLayout";
import { AddMeterPage } from "./pages/customer/settings/energy-profile/add-meter/AddMeterPage";
import { BROADCAST_NOTIFICATION_ROUTES } from "./globals/BroadcastNotificationRoutes";

const RouteRedirect = ({ to, preserveParams = false }: { to: string; preserveParams?: boolean }) => {
	const params = useParams();

	if (preserveParams && params.id) {
		return <Navigate to={`/${to}/${params.id}`} replace />;
	}

	return <Navigate to={`/${to}`} replace />;
};

export const appRoutes = [
	{
		element: (
			<>
				<ScrollToTop />
				<LogoutHandler />
			</>
		),
		children: [
			{ path: "/landing-page", element: <LandingPageV2 /> },
			{
				path: `/`,
				element: <CustomerPublicLayout />,
				children: [
					{
						index: true,
						element: <Navigate to={ApplicationRoutes.CUSTOMER_SIGNIN} replace />,
					},
					{
						path: ApplicationRoutes.CUSTOMER_SIGNUP,
						element: <CustomerSignUpPage />,
					},
					{
						path: ApplicationRoutes.WELCOME_SCREEN,
						element: <WelcomeScreen />,
					},
					{
						path: ApplicationRoutes.CUSTOMER_SIGNIN,
						element: <CustomerSignInPage />,
					},
					{
						path: ApplicationRoutes.CUSTOMER_FORGOT_PASSWORD,
						element: <CustomerForgotPasswordPage />,
					},
					{
						path: ApplicationRoutes.CUSTOMER_RESET_SUCCESS,
						element: <PasswordResetSuccessPage />,
					},
					{
						path: ApplicationRoutes.PASSCODE_SIGNIN,
						element: <CustomerPassCodeLoginPage />,
					},
				],
			},
			{
				path: ApplicationRoutes.LANDING_PAGE,
				element: <PublicLayoutLanding />,
				children: [
					{
						path: "",
						element: <LandingPage />,
					},
					{
						path: ApplicationRoutes.TERMS,
						element: <Terms />,
					},
					{
						path: ApplicationRoutes.PRIVACY_POLICY,
						element: <PrivacyPolicyPage />,
					},
					{
						path: ApplicationRoutes.DELETE_INSTRUCTION,
						element: <DeleteInstructionPage />,
					},
					{ path: ApplicationRoutes.COMING_SOON, element: <ComingSoonPage /> },
				],
			},
			{
				path: "/test",
				element: <TestCanvas />,
			},
			{
				element: <StoreFrontLayout />,
				children: [
					{
						path: ApplicationRoutes.STORE_FRONT,
						element: <StoreFront />,
					},
					{
						path: ApplicationRoutes.STORE_FRONT_CATEGORY,
						element: <StoreFrontCategory />,
					},
					{
						path: `${ApplicationRoutes.STORE_FRONT_ITEM}/:id`,
						element: <StoreFrontItem />,
					},
					{
						element: <GuestOrCustomerGuard />,
						children: [
							{
								path: ApplicationRoutes.CONFIRM_ORDER,
								element: <ConfirmOrder />,
							},
							{
								path: ApplicationRoutes.CHECKOUT,
								element: <Checkout />,
							},
							{
								path: ApplicationRoutes.WISHLIST,
								element: <WishlistPage />,
							},
							{
								path: ApplicationRoutes.BILLING_INFO,
								element: <BillingInfoPage />,
							},
							{
								path: ApplicationRoutes.CONFIRM_PAYMENT,
								element: <ConfirmPayment />,
							},
						],
					},
				],
			},
			{
				path: ApplicationRoutes.PROVIDER_SIGNUP_CONFIRMATION,
				element: <SignUpProviderConfirmation />,
			},
			{
				element: <PublicFormLayout />,
				children: [
					{
						path: ApplicationRoutes.NEW_CUSTOMER_SIGNUP_USER_INFORMATION,
						element: <UserInfoStep />,
					},
					{
						path: ApplicationRoutes.NEW_CUSTOMER_SIGNUP_OTP,
						element: <VerifyOtpStep />,
					},
					{
						path: ApplicationRoutes.NEW_CUSTOMER_SIGNUP_NEXT_STEP,
						element: <SignUpSelectNextStep />,
					},
					{
						path: ApplicationRoutes.RECOMMENDATION,
						element: <SolarRecommendation />,
					},
				],
			},
			{
				element: <PublicLayoutProducts />,
				children: [
					{
						path: ApplicationRoutes.RECOMMENDATION_OVERVIEW,
						element: <ProductBrowserPage />,
					},
					{
						path: ApplicationRoutes.RECOMMENDATION_PRODUCTS,
						element: <ProductListPage />,
					},
					{
						path: ApplicationRoutes.RECOMMENDATION_PRODUCT_DETAILS,
						element: <SolarSystemDetails />,
					},
				],
			},
			{
				element: <PublicLayoutFinalization />,
				children: [
					{
						path: ApplicationRoutes.PROVIDER_SIGNUP,
						element: <SignUpProvider />,
					},
					{
						path: ApplicationRoutes.ADMIN_SECRET,
						element: <Navigate to={`/${ApplicationRoutes.ADMIN_BASE}`} replace />,
					},
					{
						path: `${ApplicationRoutes.ADMIN_BASE}`,
						element: <LoginProvider />,
					},
					{
						path: ApplicationRoutes.ADMIN_UTIL_SECRET,
						element: <Navigate to={`/${ApplicationRoutes.ADMIN_UTIL_BASE}`} replace />,
					},
					{
						path: `${ApplicationRoutes.ADMIN_UTIL_BASE}`,
						element: <LoginProvider />,
					},
					{
						path: ApplicationRoutes.FORGOT_PASSWORD,
						element: <ForgotPassword />,
					},
					{
						path: ApplicationRoutes.PROVIDER_TEAM_MEMBER_CONFIRMATION,
						element: <ConfirmTeamMember />,
					},
				],
			},
			{
				element: <ProviderGuard />,
				children: [
					{
						path: ApplicationRoutes.PROVIDER_BASE,
						element: <ProviderLayout />,
						children: [
							{
								path: ApplicationRoutes.DASHBOARD,
								element: <ProviderDashboard />,
							},
							{
								path: ApplicationRoutes.PROVIDER_CUSTOMERS,
								element: <ProviderCustomers />,
							},
							{
								path: `${ApplicationRoutes.PROVIDER_COMPANY_FIRST_LOGIN}:companyId`,
								element: <CompanyFirstLogin />,
							},
							{
								path: ApplicationRoutes.PRODUCTS,
								element: <ProductsPage />,
							},
							{
								path: ApplicationRoutes.CREATE_PRODUCT,
								element: <CreateProduct />,
							},
							{
								path: `${ApplicationRoutes.EDIT_PRODUCT}:id`,
								element: <EditProduct />,
							},
							{
								path: ApplicationRoutes.PROVIDER_TEAM_MEMBERS,
								element: <TeamMembers />,
							},
							{
								path: ApplicationRoutes.PROVIDER_ADD_TEAM_MEMBER,
								element: <AddTeamMember />,
							},
							{
								path: ApplicationRoutes.REQUEST,
								element: <RequestPage />,
							},
						],
					},
				],
			},
			{
				element: (
						<AdminGuard />
				),
				children: [
					{
						path: ApplicationRoutes.ADMIN_BASE,
						children: [
							{
								path: ApplicationRoutes.ADMIN_LANDING,
								element: <AdminLandingPage />,
							},
							{
								// BPS admin module — post-login landing while the picker at ADMIN_LANDING is skipped.
								element: <BpsAdminLayout />,
								children: [
									{
										path: ApplicationRoutes.DASHBOARD,
										element: <BpsAdminDashboard />,
									},
									{
										path: ApplicationRoutes.BPS_PRODUCT_ANALYTICS,
										element: <ProductAnalyticsPage />,
									},
									// Siblings rather than children of the route above: the menu nests
									// them under it, but ProductAnalyticsPage is a page, not an outlet.
									{
										path: `${ApplicationRoutes.BPS_PRODUCT_ANALYTICS}/${ApplicationRoutes.BPS_FUNNELS}`,
										element: <FunnelsPage />,
									},
									
									{
										path: ApplicationRoutes.SOLAR_PROVIDER,
										element: <BpsAdminPlaceholderPage title="Solar Provider" />,
									},
									{
										path: ApplicationRoutes.BPS_CUSTOMER,
										element: <BpsAdminPlaceholderPage title="Customer" />,
									},
									{
										path: ApplicationRoutes.SEARCH_PARAMETER,
										element: <BpsAdminPlaceholderPage title="Search Parameter" />,
									},
									{
										path: ApplicationRoutes.LOAN_SUMMARY,
										element: <BpsAdminPlaceholderPage title="Loan Summary Page" />,
									},
									{
										path: ApplicationRoutes.LOAN_PRODUCTS,
										element: <BpsAdminPlaceholderPage title="Loan Product Page" />,
									},
									{
										path: ApplicationRoutes.BPS_UTILITY_SETUP,
										element: <UtilitySetupPage />,
									},
									{
										path: ApplicationRoutes.BPS_UTILITY_SETUP_NEW,
										element: <UtilityDetailsPage />,
									},
									{
										path: ApplicationRoutes.BPS_UTILITY_SETUP_EDIT,
										element: <UtilityDetailsPage />,
									},
									{
										path: ApplicationRoutes.BPS_PAYG_OPTION_1,
										element: <BpsAdminPlaceholderPage title="Pay-As-You-Go — Option 1" />,
									},
									{
										path: ApplicationRoutes.BPS_PAYG_OPTION_2,
										element: <BpsAdminPlaceholderPage title="Pay-As-You-Go — Option 2" />,
									},
									{
										path: ApplicationRoutes.BPS_ACTIVE_PAYMENT,
										element: <BpsAdminPlaceholderPage title="Active Payment" />,
									},
									{
										path: ApplicationRoutes.SETTINGS,
										element: <BpsAdminPlaceholderPage title="Settings" />,
									},
								],
							},
							{
								path: ApplicationRoutes.SOLAR_MARKET,
								// element: <PrivateLayout />,
								element: <BillsLayout />,
								children: [
									{
										path: ApplicationRoutes.DASHBOARD,
										element: <AdminDashboard />,
									},
									{
										path: ApplicationRoutes.LOAN_PRODUCTS,
										element: <LoanProductsPage />,
									},
									{
										path: ApplicationRoutes.LOAN_REQUEST,
										element: <LoanRequestPage />,
									},
									{
										path: ApplicationRoutes.SOLAR_PROVIDER,
										element: <SolarProvider />,
									},
									{
										path: ApplicationRoutes.SEARCH_PARAMETER,
										element: <SearchParameters />,
									},
									{
										path: ApplicationRoutes.APPLIANCE_CATEGORIES,
										element: <ApplianceCategory />,
									},
									{
										path: ApplicationRoutes.CUSTOMER_BASE,
										element: <AdminCustomerPage />,
									},
									{
										path: ApplicationRoutes.LOAN_SUMMARY,
										element: <LoanSummaryPage />,
									},
									{
										path: ApplicationRoutes.PRODUCTS_CATEGORY,
										element: <ProductCategoryPage />,
									},
									{
										path: ApplicationRoutes.ROOF_TYPE,
										element: <RoofTypePage />,
									},
									{
										path: ApplicationRoutes.ADVERT_BANNER,
										element: <AdvertBannerPage />,
									},
									{
										path: ApplicationRoutes.LEADS,
										element: <LeadsPage />,
									},
								],
							},
							// Bills Routes with a different layout
							{
								path: ApplicationRoutes.ADMIN_BILLS,
								element: <BillsLayout />,
								children: [
									{
										path: ApplicationRoutes.DASHBOARD,
										element: <BillsDashboard />,
									},
									{
										path: ApplicationRoutes.VENDING_HISTORY,
										element: <VendingPage />,
									},
									{
										path: ApplicationRoutes.POSTPAID_HISTORY,
										element: <PostpaidPage />,
									},
									{
										path: ApplicationRoutes.BULK_PURCHASE,
										element: <BulkPurchasePage />,
									},
									{
										path: ApplicationRoutes.BULK_PURCHASE_DETAILS,
										element: <BulkPurchaseDetailsPage />,
									},
									{
										path: ApplicationRoutes.SETTINGS,
										element: <SettingsPage />,
									},
									// Add more bills-related routes here
								],
							},
						],
					},
				],
			},
			{
				element: (
						<UtilityAdminGuard />
				),
				children: [
					{
						path: ApplicationRoutes.ADMIN_UTIL_BASE,
						children: [
							// Module Selection Page
							{
								path: ApplicationRoutes.ADMIN_LANDING,
								element: <ModuleSelectionPage />,
							},
							// Bills Payment & Operations Module
							{
								path: ApplicationRoutes.BILLS_OPERATIONS,
								element: <UtilityLayout />,
								children: [
									{
										path: ApplicationRoutes.DASHBOARD,
										element: <UtilAdminDashboard />,
										permission: UtilityPermissions.ViewDashboard,
									},
									{
										path: ApplicationRoutes.VENDING_HISTORY,
										element: <VendingPageUtilityAdmin />,
										permission: UtilityPermissions.ViewPrepaidTransactions,
									},
									{
										path: ApplicationRoutes.POSTPAID_HISTORY,
										element: <PostpaidPageUtilityAdmin />,
										permission: UtilityPermissions.ViewPostpaidTransactions,
									},
									{
										path: ApplicationRoutes.BULK_PURCHASE,
										element: <BulkPurchasePageUtilityAdmin />,
										permission: UtilityPermissions.ViewBulkTransaction,
									},
									{
										path: ApplicationRoutes.BULK_PURCHASE_DETAILS,
										element: <BulkPurchaseDetailsPage />,
										permission: UtilityPermissions.ViewBulkTransaction,
									},
									{
										path: ApplicationRoutes.CUSTOMER_360,
										element: <CustomerPage />,
										permission: UtilityPermissions.ViewCustomerTable,
									},
									{
										path: ApplicationRoutes.RECONCILLIATION,
										element: <Reconcilliation />,
										permission: UtilityPermissions.ViewReconcilliation,
									},
									{
										path: ApplicationRoutes.REFUNDS,
										element: <RefundsPage />,
										permission: UtilityPermissions.ViewRefunds,
									},
									{
										path: ApplicationRoutes.REFUND_DETAILS,
										element: <RefundDetailsPage />,
										permission: UtilityPermissions.ViewRefunds,
									},
								],
							},
							// Service Request Module
							{
								path: ApplicationRoutes.SERVICE_REQUEST_MODULE,
								element: <UtilityLayout />,
								children: [
									{
										path: ApplicationRoutes.DASHBOARD,
										element: <ServiceRequestDashboardPage />,
										permission: UtilityPermissions.ViewServiceRequestDashboard,
									},
									{
										path: ApplicationRoutes.ACTIVITY_LOG,
										element: <ActivityLogPage />,
									},
									{
										path: ApplicationRoutes.SERVICE_REQUEST,
										element: <ServiceRequestTypesPage />,
										permission: UtilityPermissions.ViewServiceRequestDefinition,
									},
									{
										path: `${ApplicationRoutes.SERVICE_REQUEST}/${ApplicationRoutes.NEW}`,
										element: <NewServiceTypePage />,
										permission: UtilityPermissions.CreateServiceRequestDefinition,
									},
									{
										path: `${ApplicationRoutes.SERVICE_REQUEST}/${ApplicationRoutes.NEW}/${ApplicationRoutes.CUSTOM_FORM}`,
										element: <CustomFormPage />,
										permission: UtilityPermissions.CreateServiceRequestDefinition,
									},
									{
										path: `${ApplicationRoutes.SERVICE_REQUEST}/${ApplicationRoutes.NEW}/${ApplicationRoutes.CUSTOM_FORM}/${ApplicationRoutes.PAYMENT_OPTION}`,
										element: <PaymentOptionPage />,
										permission: UtilityPermissions.CreateServiceRequestDefinition,
									},
									{
										path: `${ApplicationRoutes.SERVICE_REQUEST}/${ApplicationRoutes.EDIT}`,
										element: <NewServiceTypePage />,
										permission: UtilityPermissions.EditServiceRequestDefinition,
									},
									{
										path: `${ApplicationRoutes.SERVICE_REQUEST}/${ApplicationRoutes.EDIT}/${ApplicationRoutes.CUSTOM_FORM}`,
										element: <CustomFormPage />,
										permission: UtilityPermissions.EditServiceRequestDefinition,
									},
									{
										path: `${ApplicationRoutes.SERVICE_REQUEST}/${ApplicationRoutes.EDIT}/${ApplicationRoutes.CUSTOM_FORM}/${ApplicationRoutes.PAYMENT_OPTION}`,
										element: <PaymentOptionPage />,
										permission: UtilityPermissions.EditServiceRequestDefinition,
									},
									{
										path: `${ApplicationRoutes.SERVICE_REQUEST}/:id/${ApplicationRoutes.INSTANCES}`,
										element: <ServiceRequestInstancesPage />,
										permission: UtilityPermissions.ViewServiceRequestInstanceDetails,
									},
									{
										path: `${ApplicationRoutes.SERVICE_REQUEST}/:id/${ApplicationRoutes.INSTANCES}/:instanceId`,
										element: <ServiceRequestDetailsPage />,
										permission: UtilityPermissions.ViewServiceRequestInstanceDetails,
									},

									{
										path: ApplicationRoutes.WORKFLOWS_MANAGMENT,
										element: <WorkflowsManagmentPage />,
										permission: UtilityPermissions.ViewWorkflowTable,
									},
									{
										path: `${ApplicationRoutes.WORKFLOWS_MANAGMENT}/${ApplicationRoutes.NEW}/:workflowId`,
										element: <NewWorkflowPage />,
										permission: UtilityPermissions.CreateWorkflow,
									},
									{
										path: `${ApplicationRoutes.WORKFLOWS_MANAGMENT}/${ApplicationRoutes.NEW}`,
										element: <NewWorkflowPage />,
										permission: UtilityPermissions.CreateWorkflow,
									},
									{
										path: `${ApplicationRoutes.WORKFLOWS_MANAGMENT}/:workflowId/${ApplicationRoutes.CUSTOM_STEPS}`,
										element: <CustomStepsPage />,
										permission: UtilityPermissions.EditWorkflow,
									},
									{
										path: `${ApplicationRoutes.WORKFLOWS_MANAGMENT}/${ApplicationRoutes.STEP_INSTANCE}/:StepInstanceId`,
										element: <StepInstancePage />,
										permission: UtilityPermissions.ViewStaffServiceRequestSteps,
									},
									{
										path: ApplicationRoutes.STEPS,
										element: <DepartmentSelectionPage />,
										permission: UtilityPermissions.ViewStaffServiceRequestSteps,
									},
									{
										path: `${ApplicationRoutes.STEPS}/:departmentId`,
										element: <StepsPage />,
										permission: UtilityPermissions.ViewStaffServiceRequestSteps,
									},
									{
										path: ApplicationRoutes.AUDIT_LOG,
										element: <AdminUtilAuditLogPage />,
										permission: UtilityPermissions.ViewAuditLogs,
									},
									{
										path: ApplicationRoutes.SETTINGS,
										element: <UtilSettingsPage />,
										permission: UtilityPermissions.ViewUtilitySettings,
									},
									{
										path: ApplicationRoutes.DEPARTMENT_CONFIGURATION,
										element: (
											<AdminFeatureGate feature={FeatureFlagName.ADMIN_SERVICE_REQUEST_WITH_DEPARTMENT} title="Departments">
												<DepartmentsPage />
											</AdminFeatureGate>
										),
										permission: UtilityPermissions.ViewDepartment,
									},
									{
										path: `${ApplicationRoutes.DEPARTMENT_CONFIGURATION}/${ApplicationRoutes.CREATE_DEPARTMENT}`,
										element: (
											<AdminFeatureGate feature={FeatureFlagName.ADMIN_SERVICE_REQUEST_WITH_DEPARTMENT} title="Departments">
												<CreateDepartmentPage />
											</AdminFeatureGate>
										),
										permission: UtilityPermissions.CreateDepartment,
									},
									{
										path: `${ApplicationRoutes.DEPARTMENT_CONFIGURATION}/:id`,
										element: (
											<AdminFeatureGate feature={FeatureFlagName.ADMIN_SERVICE_REQUEST_WITH_DEPARTMENT} title="Departments">
												<DepartmentDetailPage />
											</AdminFeatureGate>
										),
										permission: UtilityPermissions.ViewDepartment,
									},
									{
										path: `${ApplicationRoutes.REFERAL_SYSTEM}`,
										element: <SystemReferralPage />,
									},
									{
										path: `${ApplicationRoutes.STAFF_DETAILS}`,
										element: (
											<AdminFeatureGate feature={FeatureFlagName.ADMIN_REFERRAL_SYSTEM} title="Referral System">
												<StaffDetailsPage />
											</AdminFeatureGate>
										),
									},
								],
							},
							// Customer Support Module
							{
								path: ApplicationRoutes.CUSTOMER_SUPPORT,
								element: <UtilityLayout />,
								children: [
									{
										index: true,
										element: <Navigate to={ApplicationRoutes.ALL_INSTANCES} replace />,
									},
									{
										path: ApplicationRoutes.ALL_INSTANCES,
										element: <AllServiceRequestInstancesPage />,
										permission: UtilityPermissions.ViewServiceRequestSupportDesk,
									},
									{
										path: ApplicationRoutes.REPORT_SERVICE_REQUEST,
										element: (
											<AdminFeatureGate feature={FeatureFlagName.ADMIN_CREATE_SERVICE_REQUEST} title="Create Service Request">
												<SuperAgentCreateServiceRequestPage />
											</AdminFeatureGate>
										),
										permission: UtilityPermissions.CreateServiceRequestSupportDesk,
									},
									{
										path: ApplicationRoutes.TRACK_SERVICE_REQUEST,
										element: <ServiceRequestInstancesPage />,
										permission: UtilityPermissions.ViewServiceRequestSupportDesk,
									},
								],
							},
							// Client Management Module
							{
								path: ApplicationRoutes.CLIENT_MANAGEMENT,
								element: <UtilityLayout />,
								children: [
									{
										path: ApplicationRoutes.CUSTOMER_360,
										element: <CustomerPage />,
										permission: UtilityPermissions.ViewCustomerTable,
									},
									{
										path: ApplicationRoutes.CUSTOMER_PROFILE,
										element: <CustomerProfilePage />,
									},
									{
										path: ApplicationRoutes.METERS_ACCOUNTS,
										element: <MetersAccountsPage />,
									},
									{
										path: ApplicationRoutes.BILLING_HISTORY,
										element: <BillingHistoryPage />,
									},
									{
										path: ApplicationRoutes.SERVICE_REQUEST_HISTORY,
										element: <ServiceRequestHistoryPage />,
									},
									{
										path: ApplicationRoutes.INTERACTION_LOGS,
										element: <InteractionLogsPage />,
									},
								],
							},
						],
					},
				],
			},
			{
				element: (
						<CustomerGuard />
				),
				children: [
					{
						path: ApplicationRoutes.CUSTOMER_BASE,
						element: <CustomerLayout />,
						children: [
							{
								path: ApplicationRoutes.DASHBOARD,
								element: <CustomerDashboard />,
							},
							{
								path: ApplicationRoutes.BUY_ELECTRICITY,
								element: <FeatureGuard feature={FeatureFlagName.BUY_ELECTRICITY}><BuyElectricityPage /></FeatureGuard>,
							},
							{
								path: ApplicationRoutes.CUSTOMER_BULK_PURCHASE,
								element: <FeatureGuard feature={FeatureFlagName.BULK_ELECTRICITY_PURCHASE}><CustomerBulkPurchasePage /></FeatureGuard>,
							},
							{
								path: ApplicationRoutes.CUSTOMER_SAVED_BENEFICIARIES,
								element: <FeatureGuard feature={FeatureFlagName.SAVED_BENEFICIARIES}><SavedBeneficiariesPage /></FeatureGuard>,
							},
							{
								path: ApplicationRoutes.CUSTOMER_SAVED_BENEFICIARIES_ADD,
								element: <FeatureGuard feature={FeatureFlagName.SAVED_BENEFICIARIES}><AddBeneficiaryPage /></FeatureGuard>,
							},
							{
								path: ApplicationRoutes.CUSTOMER_SAVED_BENEFICIARIES_DETAIL,
								element: <FeatureGuard feature={FeatureFlagName.SAVED_BENEFICIARIES}><BeneficiaryDetailsPage /></FeatureGuard>,
							},
							{
								path: ApplicationRoutes.TRANSFER_UNIT,
								element: <FeatureGuard feature={FeatureFlagName.TRANSFER_UNIT}><TransferUnitIntroPage /></FeatureGuard>,
							},
							{
								path: ApplicationRoutes.TRANSFER_UNIT_VERIFY,
								element: <FeatureGuard feature={FeatureFlagName.TRANSFER_UNIT}><TransferUnitVerifyMeterPage /></FeatureGuard>,
							},
							{
								path: ApplicationRoutes.TRANSFER_UNIT_RECIPIENT,
								element: <FeatureGuard feature={FeatureFlagName.TRANSFER_UNIT}><TransferUnitRecipientPage /></FeatureGuard>,
							},
							{
								path: ApplicationRoutes.TRANSFER_UNIT_AMOUNT,
								element: <FeatureGuard feature={FeatureFlagName.TRANSFER_UNIT}><TransferUnitAmountPage /></FeatureGuard>,
							},
							{
								path: ApplicationRoutes.CUSTOMER_GET_SOLAR,
								element: <GetSolarPage />,
							},
							{
								path: ApplicationRoutes.CUSTOMER_TRANSACTION_HISTORY,
								element: <CustomerTransactionHistoryPage />,
							},

							{
								path: ApplicationRoutes.CUSTOMER_SUPPORT_PAGE,
								element: <SupportLayout />,
								children: [
									{
										index: true,
										element: <SupportPage />,
									},
									{
										path: ApplicationRoutes.CUSTOMER_CALL_CENTER,
										element: <CallCenter />,
									},
								],
							},


							{
								path: ApplicationRoutes.CUSTOMER_TRANSACTION_HISTORY_FILTER,
								element: <CustomerTransactionFilterPage />,
							},
							{
								path: ApplicationRoutes.CUSTOMER_TRANSACTION_HISTORY_DETAIL,
								element: <CustomerTransactionHistoryDetailsPage />,
							},
							{
								element: <EnergyProfileGuard />,
								children: [
									{
										path: ApplicationRoutes.CUSTOMER_ENERGY_MANAGEMENT,
										element: <EnergyManagement />,
									},
									{
										path: `${ApplicationRoutes.CUSTOMER_ENERGY_MANAGEMENT}/${ApplicationRoutes.CUSTOMER_UNIT_THRESHOLD_SETUP}`,
										element: <FeatureGuard feature={FeatureFlagName.UNIT_TRESHOLD_REMINDER}><UnitThresholdSetupPage /></FeatureGuard>,
									},
									{
										path: `${ApplicationRoutes.CUSTOMER_ENERGY_MANAGEMENT}/${ApplicationRoutes.CUSTOMER_CONSUMPTION_ANALYTICS}`,
										element: <ConsumptionAnalyticsPage />,
									},
								],
							},
							{
								path: ApplicationRoutes.CUSTOMER_SOLAR_CONSUMPTION,
								element: <SolarConsumption />,
							},
							{
								path: ApplicationRoutes.CUSTOMER_ESTIMATED_BILL,
								element: <EstimatedBill />,
							},
							{
								path: ApplicationRoutes.CUSTOMER_ORDERS,
								element: <CustomerOrders />,
							},
							{
								path: ApplicationRoutes.CUSTOMER_SOLAR_JOURNEY,
								element: <SolarJourneyPage />,
							},
							{
								path: ApplicationRoutes.CUSTOMER_NOTIFICATIONS,
								element: <NotificationPage />,
							},
							{
								path: ApplicationRoutes.CUSTOMER_NOTIFICATION_DETAIL,
								element: <NotificationDetailPage />,
							},

							{
								element: <EnergyProfileGuard />,
								children: [
									{
										path: ApplicationRoutes.CUSTOMER_ACCOUNT_ACTIVITIES,
										element: <FeatureGuard feature={FeatureFlagName.STATEMENT_OF_ACCOUNT}><AccountActivitiesPage /></FeatureGuard>,
									},
									{
										path: ApplicationRoutes.CUSTOMER_ACCOUNT_ACTIVITIES_NIN,
										element: <AccountActivitiesNinVerificationPage />,
									},
									{
										path: ApplicationRoutes.CUSTOMER_HISTORICAL_PAYMENT,
										element: <HistoricalPaymentPage />,
									},
									{
										path: ApplicationRoutes.CUSTOMER_HISTORICAL_PAYMENT_FILTER,
										element: <CustomerTransactionFilterPage showCategory={false} showStatus={false} />,
									},
									{
										path: ApplicationRoutes.CUSTOMER_HISTORICAL_PAYMENT_DETAIL,
										element: <HistoricalPaymentDetailPage />,
									},
									{
										path: ApplicationRoutes.SERVICE_REQUEST_MODULE,
										element: <FeatureGuard feature={FeatureFlagName.SERVICE_REQUEST}><ServiceRequestPage /></FeatureGuard>,
									},
									{
										path: ApplicationRoutes.CUSTOMER_ALL_SERVICE_REQUESTS,
										element: <AllServiceRequestsPage />,
									},
									{
										path: ApplicationRoutes.CUSTOMER_SERVICE_REQUEST_DETAIL,
										element: <ServiceRequestDetailPage />,
									},
									{
										path: ApplicationRoutes.CUSTOMER_SERVICE_REQUEST_CREATE,
										element: <SubmitServiceRequestPage />,
									},
								],
							},

							{
								path: ApplicationRoutes.PAYMENTS,
								element: <CustomerPaymentsPage />,
							},
							{
								path: ApplicationRoutes.CUSTOMER_WALLET,
								element: <WalletPage />,
							},
							{
								path: ApplicationRoutes.CUSTOMER_WALLET_HISTORY,
								element: <WalletHistoryPage />,
							},

							{
								path: "settings",
								element: <SettingsLayout />,
								children: [
									{
										index: true,
										element: <SettingsRedirect />,
									},
									{
										path: ApplicationRoutes.PROFILE_DETAILS,
										element: <ProfileDetailsPage />,
									},

									// TODO: remove this route after the old energy profile page is removed
									// {
									// 	path: "energy-profile",
									// 	element: <EnergyProfileOld />,
									// },

									{
										path: ApplicationRoutes.ENERGY_PROFILE_NIN,
										element: <NinVerificationPage />,
									},

									{
										path: ApplicationRoutes.PROFILE_PICTURE,
										element: <ProfilePicturePage />,
									},
									{
										path: ApplicationRoutes.TEAM_MANAGEMENT,
										element: <TeamManagementPage />,
									},
									{
										path: ApplicationRoutes.SECURITY,
										element: <SecurityPage />,
									},
									{
										path: `${ApplicationRoutes.TEAM_MANAGEMENT}/${ApplicationRoutes.INVITE_TEAM_MEMBER}`,
										element: <InviteTeamMemberPage />,
									},
									{
										path: `${ApplicationRoutes.TEAM_MANAGEMENT}/${ApplicationRoutes.MANAGE_TEAM_MEMBERS}`,
										element: <ManageTeamMembersPage />,
									},
									{
										path: ApplicationRoutes.CUSTOMER_APPEARANCE,
										element: <AppearancePage />,
									},
									{
										path: ApplicationRoutes.APP_INFORMATION,
										element: <AppInformationPage />,
									},
									{
										path: ApplicationRoutes.REMOTE_TOKEN,
										element: <RemoteTokenPage />,
										feature: FeatureFlagName.REMOTE_TOKEN_LOAD,
									},
									{
										path: ApplicationRoutes.CUSTOMER_FAQ,
										element: <FaqPage />,
									},
									{
										path: ApplicationRoutes.CUSTOMER_LEGAL,
										element: <LegalPage />,
									},
									{
										element: <EnergyProfileGuard />,
										children: [
											{
												path: ApplicationRoutes.ENERGY_PROFILE_WELCOME,
												element: <EnergyProfileWelcomePage />,
											},
											{
												path: ApplicationRoutes.ENERGY_PROFILE_CREATE,
												element: <EnergyProfileCreatePage />,
											},
											{
												path: ApplicationRoutes.CUSTOMER_ENERGY_PROFILE,
												element: <SwitchMeterNumberPage />,
											},
											{
												path: `${ApplicationRoutes.CUSTOMER_ENERGY_PROFILE}/${ApplicationRoutes.CUSTOMER_MANAGE_METERS}`,
												element: <ManageMetersPage />,
											},
											{
												path: `${ApplicationRoutes.CUSTOMER_ENERGY_PROFILE}/${ApplicationRoutes.ADD_METER}`,
												element: <AddMeterPage />,
											},
										],
									},
								],
							},
						],
					},
					{
						element: <PublicLayoutProducts />,
						children: [
							{
								path: ApplicationRoutes.RECOMMENDATION_OVERVIEW,
								element: <ProductBrowserPage />,
							},
							{
								path: ApplicationRoutes.RECOMMENDATION_PRODUCTS,
								element: <ProductListPage />,
							},
							{
								path: ApplicationRoutes.RECOMMENDATION_PRODUCT_DETAILS,
								element: <SolarSystemDetails />,
							},
						],
					},
					{
						element: <PublicLayoutFinalization />,
						children: [
							// {
							// 	path: ApplicationRoutes.RECOMMENDATION_FINALIZATION,
							// 	element: <SolarRecommendationFinalization />,
							// },
							{
								path: ApplicationRoutes.RECOMMENDATION_PRODUCT_CONFIRMATION,
								element: <SolarProductConfirmation />,
							},
						],
					},
					{
						element: <PublicFormLayout />,
						children: [
							{
								path: ApplicationRoutes.NEW_CUSTOMER_SIGNUP_NEXT_STEP,
								element: <SignUpSelectNextStep />,
							},
							{
								path: ApplicationRoutes.CUSTOMER_NEXT_STEP,
								element: <SelectNextStep />,
							},
						],
					},
				],
			},
			{
				element: <UnknownCustomerLayout />,
				children: [
					{
						path: `${ApplicationRoutes.FAMILY_MEMBER_SUBMIT_DETAILS}/:id`,
						element: <RouteRedirect to={ApplicationRoutes.TEAM_MEMBER_SUBMIT_DETAILS} preserveParams={true} />,
					},
					{
						path: `${ApplicationRoutes.TEAM_MEMBER_SUBMIT_DETAILS}/:id`,
						element: <TeamMemberSubmitDetailsPage />,
					},
					{
						path: ApplicationRoutes.PAY_REQUEST,
						element: <PayRequestPage />,
					},
					{
						path: `${ApplicationRoutes.GUEST_BILL_PAYMENT_DEEPLINK}/:id`,
						element: <RouteRedirect to={ApplicationRoutes.GUEST_BILL_PAYMENT} preserveParams={true} />,
					},
					{
						path: `${ApplicationRoutes.GUEST_BILL_PAYMENT}/:token`,
						element: <GuestBillPaymentPage />,
					},
				],
			},
			{
				path: `${ApplicationRoutes.UTIL_ADMIN_SIGNUP}/:token`,
				element: (
						<CompleteSignup />
				),
			},
			...BROADCAST_NOTIFICATION_ROUTES.map(({ routeName, eqivalentWebRoute }) => ({
				path: routeName,
				loader: () => replace(eqivalentWebRoute),
			})),
			{
				path: "*",
				element: <RouteDoesntExist />,
			},
		],
	},
];

export const router = createBrowserRouter(withAccess(appRoutes));
